import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import openSocket from "../../services/socket-io";
import { getToken, setToken, clearToken } from "../../services/tokenStore";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Interceptors must be registered synchronously (not inside a useEffect),
// otherwise child components can fire requests before they're attached,
// racing the request past the Authorization header. Since useAuth is only
// ever instantiated once (the root AuthProvider), a module-level guard
// keeps registration idempotent across re-renders without deferring it.
let interceptorsRegistered = false;
let refreshPromise = null;
let onSessionExpired = () => {};

if (!interceptorsRegistered) {
	interceptorsRegistered = true;

	api.interceptors.request.use(
		config => {
			const token = getToken();
			if (token) {
				config.headers["Authorization"] = `Bearer ${token}`;
			}
			return config;
		},
		error => {
			return Promise.reject(error);
		}
	);

	// Derruba a sessão e manda para o login. Um único lugar, porque isso
	// precisa acontecer igual venha de onde vier.
	const encerrarSessao = () => {
		// Uma sessão vencida derruba várias chamadas de uma vez (a que falhou e
		// a renovação). Sem esta saída, o aviso aparecia repetido.
		if (!getToken()) return;

		clearToken();
		api.defaults.headers.Authorization = undefined;
		onSessionExpired();
	};

	// O backend usa 403 para duas coisas diferentes: token de acesso vencido
	// (mensagem "Invalid token...") e falta de permissão (ERR_NO_PERMISSION,
	// ERR_USER_INACTIVE, ERR_READ_ONLY_USER). Só o primeiro pede renovação —
	// tentar renovar num 403 de permissão gasta uma chamada à toa e some com
	// o erro que o usuário precisava ver.
	const ehTokenVencido = resposta => {
		const motivo =
			resposta?.data?.error || resposta?.data?.message || "";

		return (
			resposta?.status === 403 &&
			!String(motivo).startsWith("ERR_NO_PERMISSION") &&
			!String(motivo).startsWith("ERR_USER_INACTIVE") &&
			!String(motivo).startsWith("ERR_READ_ONLY_USER")
		);
	};

	api.interceptors.response.use(
		response => {
			return response;
		},
		async error => {
			const originalRequest = error.config;

			// A própria renovação falhando não pode entrar em recursão.
			const ehChamadaDeRefresh = String(originalRequest?.url || "").includes(
				"/auth/refresh_token"
			);

			if (
				ehTokenVencido(error?.response) &&
				!originalRequest._retry &&
				!ehChamadaDeRefresh
			) {
				originalRequest._retry = true;

				if (!refreshPromise) {
					refreshPromise = api
						.post("/auth/refresh_token")
						.then(({ data }) => {
							if (data) {
								// Um lugar só: quem precisa do token (axios e socket)
								// escuta o tokenStore.
								setToken(data.token);
								api.defaults.headers.Authorization = `Bearer ${data.token}`;
							}
							return data;
						})
						.finally(() => {
							refreshPromise = null;
						});
				}

				try {
					await refreshPromise;
				} catch (falhaAoRenovar) {
					// O refresh token também venceu: não há como continuar. Antes
					// isso apenas propagava o erro e a pessoa ficava numa tela
					// quebrada, clicando em coisas que respondiam 403.
					encerrarSessao();
					return Promise.reject(falhaAoRenovar);
				}

				return api(originalRequest);
			}

			// 401 nem sempre quer dizer "sua sessão acabou".
			//
			// Qualquer rota que confira senha responde 401 quando ela não bate —
			// e derrubar a sessão por isso expulsava do sistema quem só errou a
			// digitação numa confirmação. Foi o que acontecia ao gerar backup.
			//
			// A sessão só cai quando o erro é da própria sessão. Erro sem código
			// reconhecido continua derrubando: é o caso do token ausente ou
			// inválido, em que o backend responde antes de qualquer regra.
			const ERROS_DE_SESSAO = [
				"ERR_SESSION_EXPIRED",
				"ERR_INVALID_TOKEN",
				"ERR_USER_INACTIVE"
			];

			if (error?.response?.status === 401) {
				const codigo = String(
					error?.response?.data?.error || error?.response?.data?.message || ""
				);

				// Resposta em blob (download) não deixa ler o código aqui; nesse
				// caso vale a regra antiga, porque não há como distinguir.
				const ehBlob = error?.response?.data instanceof Blob;

				if (!codigo || ehBlob || ERROS_DE_SESSAO.some(e => codigo.startsWith(e))) {
					encerrarSessao();
				}
			}

			return Promise.reject(error);
		}
	);
}

const useAuth = () => {
	const navigate = useNavigate();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});
	const [isImpersonating, setIsImpersonating] = useState(() => {
		return Boolean(sessionStorage.getItem("superadmin_backup"));
	});

	useEffect(() => {
		onSessionExpired = () => {
			// Tem de ser lido antes: o setIsAuth abaixo faz o <Route> privado
			// redirecionar na hora, e aí a rota já seria /login.
			const estavaLogado = window.location.pathname !== "/login";

			sessionStorage.removeItem("superadmin_backup");
			setIsImpersonating(false);
			setIsAuth(false);
			setUser({});

			if (estavaLogado) {
				toast.info(i18n.t("auth.toasts.expired"));
				// O <Route> já leva para /login, mas só quando re-renderiza. Se a
				// sessão caiu numa chamada de fundo, sem isto a tela ficava parada.
				navigate("/login");
			}
		};
	}, [navigate]);

	// Ao abrir a página, quem diz se há sessão é o cookie httpOnly `jrt` — o
	// token de acesso vive só na memória e some a cada recarga. Por isso a
	// tentativa é sempre feita: não há mais nada no navegador para consultar
	// antes.
	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.post("/auth/refresh_token");
				setToken(data.token);
				api.defaults.headers.Authorization = `Bearer ${data.token}`;
				setIsAuth(true);
				setUser(data.user);
			} catch (err) {
				// Sem cookie válido é só uma visita não autenticada — a tela de
				// login é o destino normal, não um erro para mostrar na cara de
				// quem ainda nem tentou entrar.
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === user.id) {
				setUser(data.user);
			}
		});

		return () => {
			socket.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user.id]);

	const handleLogin = async userData => {
		try {
			const { data } = await api.post("/auth/login", userData);
			if (data.requires2FA) {
				return { requires2FA: true };
			}
			sessionStorage.removeItem("superadmin_backup");
			setIsImpersonating(false);
			setToken(data.token);
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			navigate("/tickets");
			return { success: true };
		} catch (err) {
			toastError(err);
			throw err;
		}
	};

	const handleLogout = async () => {
		try {
			await api.delete("/auth/logout");
		} catch (err) {
			toastError(err);
		} finally {
			sessionStorage.removeItem("superadmin_backup");
			setIsImpersonating(false);
			setIsAuth(false);
			setUser({});
			clearToken();
			api.defaults.headers.Authorization = undefined;
			navigate("/login");
		}
	};

	const handleImpersonate = async companyId => {
		setLoading(true);
		try {
			const currentToken = getToken();
			sessionStorage.setItem(
				"superadmin_backup",
				JSON.stringify({ token: currentToken, user })
			);
			const { data } = await api.post(`/companies/${companyId}/impersonate`);
			setToken(data.token);
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsImpersonating(true);
			toast.success(`Conectado à empresa como ${data.user.name}`);
			navigate("/tickets");
		} catch (err) {
			sessionStorage.removeItem("superadmin_backup");
			setIsImpersonating(false);
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	const handleStopImpersonate = async () => {
		setLoading(true);
		try {
			const backupStr = sessionStorage.getItem("superadmin_backup");

			if (backupStr) {
				const backup = JSON.parse(backupStr);
				const { data } = await api.post(
					"/companies/stop-impersonate",
					{},
					{
						headers: {
							Authorization: `Bearer ${backup.token}`
						}
					}
				);
				setToken(data.token);
				api.defaults.headers.Authorization = `Bearer ${data.token}`;
				setUser(data.user);
			}

			sessionStorage.removeItem("superadmin_backup");
			setIsImpersonating(false);
			toast.info("Retornado à sua conta de superadministrador");
			navigate("/companies");
		} catch (err) {
			sessionStorage.removeItem("superadmin_backup");
			setIsImpersonating(false);
			toastError(err);
			navigate("/companies");
		} finally {
			setLoading(false);
		}
	};

	return {
		isAuth,
		user,
		loading,
		isImpersonating,
		handleLogin,
		handleLogout,
		handleImpersonate,
		handleStopImpersonate
	};
};

export default useAuth;
